var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isEmptyFilter } from '../../FiltersBar/utils';
import { defaultRowsPerPage } from '../types';
export function createUseDataTableFunction(hookConfig) {
    const hookStateValues = Object.values(hookConfig);
    const needUrl = hookStateValues.includes('url');
    const needHistory = hookStateValues.includes('history');
    const needState = hookStateValues.includes('state');
    const useHook = (params) => {
        let location = undefined;
        let navigate = undefined;
        let searchParams = undefined;
        if (needUrl || needHistory) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            location = useLocation();
            // eslint-disable-next-line react-hooks/rules-of-hooks
            navigate = useNavigate();
            if (needUrl) {
                searchParams = new URLSearchParams(location.search);
            }
        }
        let internalState = undefined;
        let setInternalState = undefined;
        if (needState) {
            // eslint-disable-next-line react-hooks/rules-of-hooks
            [internalState, setInternalState] = React.useState(() => {
                const initialFilterData = params.initialFilterData && hookConfig.filterState === 'state';
                const initialSelectedRowIds = params.initialSelectedRowIds && hookConfig.selectionState === 'state';
                if (initialFilterData || initialSelectedRowIds) {
                    const initialState = {};
                    if (initialFilterData) {
                        initialState.filter = params.initialFilterData;
                    }
                    if (initialSelectedRowIds) {
                        initialState.selectedRowIds = params.initialSelectedRowIds;
                    }
                    return initialState;
                }
                else {
                    return emptyObject;
                }
            });
        }
        const changeUrl = React.useCallback((changeSearchParams) => {
            var _a, _b;
            const newSearchParams = new URLSearchParams(location === null || location === void 0 ? void 0 : location.search);
            const newState = changeSearchParams(newSearchParams, (_a = location === null || location === void 0 ? void 0 : location.state) !== null && _a !== void 0 ? _a : emptyObject);
            navigate === null || navigate === void 0 ? void 0 : navigate({ pathname: '.', search: newSearchParams.toString() }, { state: Object.assign(Object.assign({}, (newState ? newState : ((_b = location === null || location === void 0 ? void 0 : location.state) !== null && _b !== void 0 ? _b : emptyObject))), { internal: true }), replace: true });
        }, [location === null || location === void 0 ? void 0 : location.search, location === null || location === void 0 ? void 0 : location.state, navigate]);
        const getHistoryOrState = (state) => {
            var _a;
            return state === 'history' ? ((_a = location === null || location === void 0 ? void 0 : location.state) !== null && _a !== void 0 ? _a : emptyObject) : internalState;
        };
        const changeHistoryOrState = React.useCallback((state, changeState) => {
            var _a;
            switch (state) {
                case 'history':
                    navigate === null || navigate === void 0 ? void 0 : navigate({ pathname: '.', search: location === null || location === void 0 ? void 0 : location.search }, {
                        state: typeof changeState === 'function'
                            ? changeState((_a = location === null || location === void 0 ? void 0 : location.state) !== null && _a !== void 0 ? _a : emptyObject)
                            : Object.assign(Object.assign({}, location === null || location === void 0 ? void 0 : location.state), changeState),
                        replace: true,
                    });
                    break;
                case 'state':
                    setInternalState(typeof changeState === 'function' ? changeState : (prevState) => (Object.assign(Object.assign({}, prevState), changeState)));
                    break;
            }
        }, [location === null || location === void 0 ? void 0 : location.search, location === null || location === void 0 ? void 0 : location.state, navigate, setInternalState]);
        const onRowClick = params.onRowClick;
        return {
            useFilterState: () => {
                var _a, _b, _c;
                if (hookConfig.filterState === 'url') {
                    const filterParam = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get('filter');
                    let filterData;
                    if (filterParam !== 'state') {
                        filterData = filterParam ? JSON.parse(filterParam) : emptyObject;
                    }
                    else {
                        const state = getHistoryOrState('history');
                        filterData = (_a = state === null || state === void 0 ? void 0 : state.filter) !== null && _a !== void 0 ? _a : emptyObject;
                    }
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const initialFilterData = React.useRef(filterData);
                    if (!((_b = location === null || location === void 0 ? void 0 : location.state) === null || _b === void 0 ? void 0 : _b.internal)) {
                        initialFilterData.current = filterData;
                    }
                    return { filterData, initialFilterData: initialFilterData.current };
                }
                else {
                    const state = getHistoryOrState(hookConfig.filterState);
                    const filterData = (_c = state === null || state === void 0 ? void 0 : state.filter) !== null && _c !== void 0 ? _c : emptyObject;
                    // eslint-disable-next-line react-hooks/rules-of-hooks
                    const initialFilterData = React.useRef(filterData);
                    return { filterData, initialFilterData: initialFilterData.current };
                }
            },
            useSortState: () => {
                var _a, _b;
                const defaultSortColumnDesc = !!params.defaultSortColumnDesc;
                if (hookConfig.sortState === 'url') {
                    const sort = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get('sort');
                    const desc = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get('desc');
                    const sortColumnKey = sort !== null && sort !== void 0 ? sort : params.defaultSortColumnKey;
                    const sortColumnDesc = desc === 'true' ? true : desc === 'false' ? false : defaultSortColumnDesc;
                    return { sortColumnKey, sortColumnDesc };
                }
                else {
                    const state = getHistoryOrState(hookConfig.sortState);
                    const sortColumnKey = (_a = state === null || state === void 0 ? void 0 : state.sort) !== null && _a !== void 0 ? _a : params.defaultSortColumnKey;
                    const sortColumnDesc = (_b = state === null || state === void 0 ? void 0 : state.desc) !== null && _b !== void 0 ? _b : defaultSortColumnDesc;
                    return { sortColumnKey, sortColumnDesc };
                }
            },
            usePageState: () => {
                var _a, _b, _c, _d;
                if (hookConfig.pageState === 'url') {
                    const pageParam = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get('page');
                    const page = pageParam ? parseInt(pageParam, 10) : 0;
                    const rowsParam = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get('rows');
                    const rowsPerPage = rowsParam ? parseInt(rowsParam, 10) : ((_a = params.defaultRowsPerPage) !== null && _a !== void 0 ? _a : defaultRowsPerPage);
                    return { page, rowsPerPage };
                }
                else {
                    const state = getHistoryOrState(hookConfig.pageState);
                    const page = (_b = state === null || state === void 0 ? void 0 : state.page) !== null && _b !== void 0 ? _b : 0;
                    const rowsPerPage = (_d = (_c = state === null || state === void 0 ? void 0 : state.rows) !== null && _c !== void 0 ? _c : params.defaultRowsPerPage) !== null && _d !== void 0 ? _d : defaultRowsPerPage;
                    return { page, rowsPerPage };
                }
            },
            useActiveState: () => {
                var _a;
                if (hookConfig.activeState === 'url') {
                    const activeRowId = (_a = searchParams === null || searchParams === void 0 ? void 0 : searchParams.get('id')) !== null && _a !== void 0 ? _a : undefined;
                    return { activeRowId };
                }
                else {
                    const state = getHistoryOrState(hookConfig.activeState);
                    const activeRowId = state === null || state === void 0 ? void 0 : state.id;
                    return { activeRowId };
                }
            },
            useSelectionState: () => {
                const state = getHistoryOrState(hookConfig.selectionState);
                const selectedRowIds = Array.isArray(state === null || state === void 0 ? void 0 : state.selectedRowIds) ? state === null || state === void 0 ? void 0 : state.selectedRowIds : emptyArray;
                return { selectedRowIds };
            },
            handleFilter: React.useCallback((newFilterData) => {
                const emptyFilter = isEmptyFilter(newFilterData);
                const changes = {
                    url: {
                        filter: false,
                        page: false,
                    },
                    history: {
                        filter: false,
                        page: false,
                    },
                    state: {
                        filter: false,
                        page: false,
                    },
                };
                const collectChanges = (state, type) => {
                    changes[state][type] = true;
                };
                collectChanges(hookConfig.filterState, 'filter');
                collectChanges(hookConfig.pageState, 'page');
                if (changes.url.filter || changes.url.page) {
                    changeUrl((newSearchParam, prevState) => {
                        const newState = Object.assign({}, prevState);
                        let returnNewState = false;
                        if (changes.url.filter) {
                            if (emptyFilter) {
                                newSearchParam.delete('filter');
                            }
                            else {
                                newSearchParam.set('filter', JSON.stringify(newFilterData));
                            }
                            changes.url.filter = false;
                        }
                        else if (changes.history.filter) {
                            if (emptyFilter) {
                                delete newState.filter;
                            }
                            else {
                                newState.filter = newFilterData;
                            }
                            returnNewState = true;
                            changes.history.filter = false;
                        }
                        if (changes.url.page) {
                            newSearchParam.delete('page');
                            changes.url.page = false;
                        }
                        else if (changes.history.page) {
                            delete newState.page;
                            returnNewState = true;
                            changes.history.page = false;
                        }
                        if (params.maxUrlSearchLength != null && newSearchParam.toString().length > params.maxUrlSearchLength) {
                            newSearchParam.set('filter', 'state');
                            newState.filter = newFilterData;
                            returnNewState = true;
                        }
                        return returnNewState ? newState : undefined;
                    });
                }
                if (changes.history.filter || changes.history.page) {
                    const history = {};
                    if (changes.history.filter) {
                        history.filter = newFilterData;
                        changes.history.filter = false;
                    }
                    if (changes.history.page) {
                        history.page = undefined;
                        changes.history.page = false;
                    }
                    changeHistoryOrState('history', history);
                }
                if (changes.state.filter || changes.state.page) {
                    const state = {};
                    if (changes.state.filter) {
                        state.filter = newFilterData;
                        changes.state.filter = false;
                    }
                    if (changes.state.page) {
                        state.page = undefined;
                        changes.state.page = false;
                    }
                    changeHistoryOrState('state', state);
                }
            }, [changeUrl, changeHistoryOrState, params.maxUrlSearchLength]),
            handlePageChange: React.useCallback((newPage) => {
                if (hookConfig.pageState === 'url') {
                    changeUrl((newSearchParam) => {
                        if (newPage === 0) {
                            newSearchParam.delete('page');
                        }
                        else {
                            newSearchParam.set('page', newPage.toString());
                        }
                    });
                }
                else {
                    changeHistoryOrState(hookConfig.pageState, { page: newPage === 0 ? undefined : newPage });
                }
            }, [changeUrl, changeHistoryOrState]),
            handleRowsPerPagePageChange: React.useCallback((newRowsPerPage) => {
                if (hookConfig.pageState === 'url') {
                    changeUrl((newSearchParam) => {
                        var _a;
                        if (newRowsPerPage === ((_a = params.defaultRowsPerPage) !== null && _a !== void 0 ? _a : defaultRowsPerPage)) {
                            newSearchParam.delete('rows');
                        }
                        else {
                            newSearchParam.set('rows', newRowsPerPage.toString());
                        }
                        newSearchParam.delete('page');
                    });
                }
                else {
                    changeHistoryOrState(hookConfig.pageState, {
                        rows: newRowsPerPage === params.defaultRowsPerPage ? undefined : newRowsPerPage,
                        page: undefined,
                    });
                }
            }, [changeUrl, changeHistoryOrState, params.defaultRowsPerPage]),
            handleSortColumnChange: React.useCallback((newSortColumnKey, newSortColumnDesc) => {
                const defaultSortColumnDesc = !!params.defaultSortColumnDesc;
                if (hookConfig.sortState === 'url') {
                    changeUrl((newSearchParam) => {
                        if (newSortColumnKey === params.defaultSortColumnKey) {
                            newSearchParam.delete('sort');
                        }
                        else {
                            newSearchParam.set('sort', newSortColumnKey);
                        }
                        if (newSortColumnDesc !== defaultSortColumnDesc) {
                            newSearchParam.set('desc', newSortColumnDesc ? 'true' : 'false');
                        }
                        else {
                            newSearchParam.delete('desc');
                        }
                    });
                }
                else {
                    changeHistoryOrState(hookConfig.sortState, {
                        sort: newSortColumnKey === params.defaultSortColumnKey ? undefined : newSortColumnKey,
                        desc: newSortColumnDesc !== defaultSortColumnDesc ? newSortColumnDesc : undefined,
                    });
                }
            }, [changeUrl, changeHistoryOrState, params.defaultSortColumnKey, params.defaultSortColumnDesc]),
            handleRowClick: React.useCallback((row) => {
                if (hookConfig.activeState === 'url') {
                    changeUrl((newSearchParam) => {
                        newSearchParam.set('id', row.id);
                    });
                }
                else {
                    changeHistoryOrState(hookConfig.activeState, { id: row.id });
                }
                onRowClick === null || onRowClick === void 0 ? void 0 : onRowClick(row);
            }, [changeUrl, changeHistoryOrState, onRowClick]),
            handleRowSelectionChange: React.useCallback((row, selected) => {
                changeHistoryOrState(hookConfig.selectionState, (prevState) => {
                    const ids = new Set(prevState.selectedRowIds);
                    if (selected) {
                        ids.add(row.id);
                    }
                    else {
                        ids.delete(row.id);
                    }
                    return Object.assign(Object.assign({}, prevState), { selectedRowIds: Array.from(ids) });
                });
            }, [changeHistoryOrState]),
            handleSelectAllRowsInCurrentPageClick: React.useCallback((rows, selected) => {
                changeHistoryOrState(hookConfig.selectionState, (prevState) => {
                    const ids = new Set(prevState.selectedRowIds);
                    if (selected) {
                        rows.forEach((row) => ids.add(row.id));
                    }
                    else {
                        rows.forEach((row) => ids.delete(row.id));
                    }
                    return Object.assign(Object.assign({}, prevState), { selectedRowIds: Array.from(ids) });
                });
            }, [changeHistoryOrState]),
            handleDeselectAllRows: React.useCallback(() => {
                changeHistoryOrState(hookConfig.selectionState, { selectedRowIds: undefined });
            }, [changeHistoryOrState]),
            clearActiveRow: React.useCallback(() => {
                if (hookConfig.activeState === 'url') {
                    changeUrl((newSearchParam) => {
                        newSearchParam.delete('id');
                    });
                }
                else {
                    changeHistoryOrState(hookConfig.activeState, { id: undefined });
                }
            }, [changeUrl, changeHistoryOrState]),
            setActiveRowId: React.useCallback((id) => {
                if (hookConfig.activeState === 'url') {
                    changeUrl((newSearchParam) => {
                        newSearchParam.set('id', id);
                    });
                }
                else {
                    changeHistoryOrState(hookConfig.activeState, { id: id });
                }
            }, [changeUrl, changeHistoryOrState]),
            setSelectedRowIds: React.useCallback((ids) => {
                changeHistoryOrState(hookConfig.selectionState, { selectedRowIds: ids });
            }, [changeHistoryOrState]),
        };
    };
    return (params) => {
        var _a;
        const _b = useHook(params), { useFilterState, useSortState, usePageState, useActiveState, useSelectionState } = _b, handlers = __rest(_b, ["useFilterState", "useSortState", "usePageState", "useActiveState", "useSelectionState"]);
        const { filterData, initialFilterData } = useFilterState();
        const { sortColumnKey, sortColumnDesc } = useSortState();
        const { page, rowsPerPage } = usePageState();
        const { activeRowId } = useActiveState();
        const { selectedRowIds } = useSelectionState();
        // Load data from backend
        const { data, isLoading, error, reload, customBackendResponseData } = params.useDataProvider({
            filterData,
            sortColumnKey,
            sortColumnDesc,
            offset: page * rowsPerPage,
            limit: rowsPerPage,
        });
        return {
            dataTableProps: {
                loading: !!isLoading,
                error: error,
                filteredRowCount: data === null || data === void 0 ? void 0 : data.filteredRowCount,
                totalRowCount: data === null || data === void 0 ? void 0 : data.totalRowCount,
                page: page,
                rowsPerPage: rowsPerPage,
                onPageChange: handlers.handlePageChange,
                onRowsPerPagePageChange: handlers.handleRowsPerPagePageChange,
                rows: (_a = data === null || data === void 0 ? void 0 : data.rows) !== null && _a !== void 0 ? _a : emptyArray,
                sortColumnKey: sortColumnKey,
                sortColumnDesc: sortColumnDesc,
                onSortColumnChange: handlers.handleSortColumnChange,
                activeRowId: params.onRowClick ? activeRowId : undefined,
                onRowClick: params.onRowClick ? handlers.handleRowClick : undefined,
                rowSelectionEnabled: params.rowSelectionEnabled,
                selectedRowIds: params.rowSelectionEnabled ? selectedRowIds : undefined,
                onRowSelectionChange: params.rowSelectionEnabled ? handlers.handleRowSelectionChange : undefined,
                onSelectAllRowsInCurrentPageClick: params.rowSelectionEnabled ? handlers.handleSelectAllRowsInCurrentPageClick : undefined,
                onDeselectAllRows: params.rowSelectionEnabled ? handlers.handleDeselectAllRows : undefined,
                filtered: !isEmptyFilter(filterData),
                customBackendResponseData: customBackendResponseData,
            },
            filtersBarProps: {
                initialFilterData: initialFilterData,
                onFilter: handlers.handleFilter,
            },
            appliedFilterData: filterData,
            clearActiveRow: handlers.clearActiveRow,
            setActiveRowId: handlers.setActiveRowId,
            setSelectedRowIds: handlers.setSelectedRowIds,
            reload: reload,
        };
    };
}
const _useDataTableUsingUrl = createUseDataTableFunction({
    filterState: 'url',
    sortState: 'url',
    pageState: 'url',
    activeState: 'url',
    selectionState: 'history',
});
const _useDataTableUsingHistory = createUseDataTableFunction({
    filterState: 'history',
    sortState: 'history',
    pageState: 'history',
    activeState: 'history',
    selectionState: 'history',
});
export const _useDataTableUsingState = createUseDataTableFunction({
    filterState: 'state',
    sortState: 'state',
    pageState: 'state',
    activeState: 'state',
    selectionState: 'state',
});
export function useDataTableUsingUrl(params) {
    return _useDataTableUsingUrl(params);
}
export function useDataTableUsingHistory(params) {
    return _useDataTableUsingHistory(params);
}
export function useDataTableUsingState(params) {
    return _useDataTableUsingState(params);
}
const emptyArray = [];
const emptyObject = {};
